import { Paging } from 'src/app/api/types/Pageable';

export interface AutoprintSubscriptions {
  [storeId: string]: any;
}

export interface AutoprintQueueElement {
  storeId: number;
  orderId: string;
  printer: PrinterInfo;
  status: string;
  tries?: number;
  /**
   * Present only on a job an automatic path queued — the order push or
   * foreground reconciliation. A job a staff member asked for by hand carries
   * none, and is never skipped as a duplicate. Card #4514.
   */
  autoprint?: AutoprintJobContext;
}

/** Which automatic path asked for a receipt. Card #4514. */
export type AutoprintOrigin = 'push' | 'recovery';

/**
 * What an automatic print job knows about the order it prints, so the queue can
 * tell a second copy of the same receipt from a receipt that is genuinely due.
 * Card #4514.
 */
export interface AutoprintJobContext {
  origin: AutoprintOrigin;
  /**
   * The order's server-side `updatedAt`, in epoch milliseconds, as the path
   * that queued the job saw it: the push's `updateAt`, or the `updatedAt` the
   * order search returned. Server time on both sides, so device clock skew
   * cannot reorder it. Absent when the path did not know it.
   */
  version?: number;
  /**
   * Reconciliation only: the `printerKey`s the pass found still owed this
   * receipt, decided once before any of its jobs was queued. The queue cannot
   * work it out again per printer: the first printer's job writes a `queued`
   * record, after which an order nothing had handled looks handled and the
   * second printer looks owed nothing.
   */
  printers?: string[];
}

/**
 * One receipt that reached a printer — or, with `state`, one an automatic path
 * queued for a printer and that has not come out yet.
 *
 * Timestamped rather than a bare uuid so the record can be aged out on the same
 * horizon foreground reconciliation looks back over, instead of on a count that
 * has no relationship to the window it guards. Card #4514.
 */
export interface PrintedOrderRecord {
  uuid: string;
  /** When this record was written (for a printed receipt, when it came out), as an ISO string. */
  printedAt: string;
  /**
   * Absent: the receipt came out. `queued`: an automatic job for it was queued
   * and has not printed yet. `failed`: the last automatic attempt failed.
   *
   * The two unfinished states are what tell reconciliation which printers still
   * owe an order a receipt. Without them, a printer added or switched back on
   * after an order printed elsewhere looked like it owed that order, and every
   * order of the last hours came out on it (card #4514).
   */
  state?: 'queued' | 'failed';
  /**
   * `printerKey` of the printer that consumed it. A device can print every
   * order on more than one printer, and one printer succeeding says nothing
   * about the other. Absent on a record written before this field existed,
   * which is read as covering every printer — the reading that cannot print a
   * receipt twice. Card #4514.
   */
  printer?: string;
  /** `AutoprintJobContext.version` of the job that printed it, when known. */
  version?: number;
}

/**
 * The identity of a registered printer.
 *
 * `url` is the key the enabled-printers list is already deduped on when a
 * printer is saved (for ESC/POS it holds the printer's name), so two entries in
 * that list never share it. `name` is the fallback for a registration without a
 * `url`. Card #4514.
 */
export function printerKey(printer: PrinterInfo | null | undefined): string {
  return printer?.url || printer?.name || '';
}

/**
 * An order's server `updatedAt` as epoch milliseconds, from either shape it
 * arrives in: the push sends `updateAt` as a string of milliseconds, the order
 * search returns `updatedAt` as an ISO date. `undefined` when it is neither.
 */
export function orderVersion(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) {
    return asNumber;
  }
  const asDate = Date.parse(value);
  return Number.isNaN(asDate) ? undefined : asDate;
}

/**
 * The newest record of the order's receipt on `printer`, if any, as
 * `mergePrintedOrderRecords` would keep it. A record without a printer predates
 * per-printer records and counts for every printer.
 */
export function latestRecordFor(
  records: PrintedOrderRecord[] | null | undefined,
  uuid: string,
  printer: string,
): PrintedOrderRecord | undefined {
  const matching = (records ?? []).filter(
    (record) =>
      record?.uuid === uuid &&
      (record.printer === undefined || record.printer === printer),
  );
  return mergePrintedOrderRecords([], matching).pop();
}

/**
 * Whether every one of `printers` has an attempt for the order that failed at
 * or after `since` (epoch ms): the jobs a dispatch at `since` started have all
 * run and come back, so it is no longer in flight. Card #4514.
 */
export function failedSince(
  records: PrintedOrderRecord[] | null | undefined,
  uuid: string,
  printers: string[],
  since: number,
): boolean {
  return (
    printers.length > 0 &&
    printers.every((printer) => {
      const latest = latestRecordFor(records, uuid, printer);
      return (
        latest?.state === 'failed' && Date.parse(latest.printedAt) >= since
      );
    })
  );
}

/**
 * Whether `records` show the order's receipt came out on the printer `printer`.
 */
export function printedRecordFor(
  records: PrintedOrderRecord[] | null | undefined,
  uuid: string,
  printer: string,
): PrintedOrderRecord | undefined {
  const latest = latestRecordFor(records, uuid, printer);
  return latest && !latest.state ? latest : undefined;
}

/**
 * Of `printers`, the ones reconciliation may still print the order on.
 *
 * - No record an automatic path left for the order: no automatic path ever got
 *   to it on this device — its push never arrived, or arrived while nothing
 *   could act on it — so every printer that was on when it came in is owed it,
 *   exactly as the push would have printed it, less any staff already printed
 *   it on by hand.
 * - Otherwise only a printer an automatic path queued it for and that has not
 *   printed it (`queued` or `failed`). A printer with no record of an order
 *   other printers did handle was not there, or not enabled, when the order
 *   came in, and is owed nothing. Card #4514.
 */
export function recoveryTargets(
  records: PrintedOrderRecord[] | null | undefined,
  uuid: string,
  printers: string[],
  ownedIfNew: (printer: string) => boolean = () => true,
): string[] {
  // Handled by an automatic path: a job queued or failed, or a receipt that
  // printed with the order version a push or a search gave it. A print staff
  // asked for by hand carries no version and does not count: it says nothing
  // about the other printers.
  const known = (records ?? []).some(
    (record) =>
      record?.uuid === uuid &&
      (!!record.state || Number.isFinite(record.version)),
  );
  if (!known) {
    // Only printers that were on when the order came in — one added or
    // switched on later was not there to print it — and not one it already
    // printed on by hand.
    return printers.filter(
      (printer) =>
        ownedIfNew(printer) && !printedRecordFor(records, uuid, printer),
    );
  }
  // A printer an automatic job was queued for owes the order whatever has
  // happened to it since — switched off and on again to clear a jam, say.
  return printers.filter(
    (printer) => !!latestRecordFor(records, uuid, printer)?.state,
  );
}

/**
 * How far apart two order versions may be and still be the same version.
 *
 * The push carries the order's `updatedAt` in milliseconds from the entity in
 * memory; the order search returns it from a column that keeps whole seconds,
 * and MySQL rounds the fraction when it stores it. So the same save can read
 * as …:28.400 on the push and …:28 or …:29 on the search. A push is a copy of a
 * printed receipt when it is no more than this newer than the printed
 * version; a backend re-announce (an isReady toggle, a payment update) is a
 * later save and prints as before. Card #4514.
 */
export const AUTOPRINT_ORDER_VERSION_TOLERANCE_MS = 1000;

/**
 * Whether `version` describes an order no newer than `seen`, at the precision
 * both sides of the comparison keep.
 */
export function isSameOrOlderVersion(
  version: number | undefined,
  seen: number | undefined,
): boolean {
  return (
    Number.isFinite(version) &&
    Number.isFinite(seen) &&
    version < seen + AUTOPRINT_ORDER_VERSION_TOLERANCE_MS
  );
}

/**
 * The order statuses foreground reconciliation recovers.
 *
 * The autoprint push is sent only for an `ORDER_SUBMITTED` order event, which
 * the backend raises for an order whose status is `SUBMITTED`. An order the
 * push was sent for can move on to `RECEIVED` or `CLOSED` before the device
 * catches up, and still needs its receipt. Everything else is left out on
 * purpose: `DRAFT` is a basket the customer never sent (the storefront creates
 * one on every visit), `CANCELLED`, `REJECTED` and `DELETED` are orders the
 * kitchen must not make, and the real-time-confirmation and marketplace states
 * are not orders the store has to make yet. Card #4514.
 */
export const AUTOPRINT_RECOVERABLE_ORDER_STATUSES = [
  'SUBMITTED',
  'RECEIVED',
  'CLOSED',
] as const;

/**
 * Hard ceiling on `AutoprintState.recentlyPrintedOrders`, as a memory and
 * storage backstop only — `AUTOPRINT_RECONCILIATION_MAX_LOOKBACK_MS` is what
 * decides which entries are still needed.
 *
 * It has to be comfortably larger than one lookback window can hold — one
 * record per order *per printer* — or a busy evening on a device with two
 * printers would push out records that are still inside the window and the
 * orders they cover would print a second time.
 */
export const RECENTLY_PRINTED_ORDERS_CAP = 2000;

/**
 * localStorage key holding the start of the window foreground reconciliation
 * asks about next, as an ISO string: the instant GoAdmin went to the background,
 * or where the last pass left off. Each pass moves it forward to its own upper
 * edge, or holds it at the oldest order it left unfinished, so it stays set
 * between passes. On the PayDroid build it moves no further than just behind
 * the newest order the search returned (card #9881). See
 * `PrintersEffects.onAutoprintAppStateChanged`.
 */
export const AUTOPRINT_RECONCILIATION_CHECKPOINT_KEY =
  'autoprintReconciliationCheckpoint';

/**
 * localStorage key holding `AutoprintState.recentlyPrintedOrders`.
 *
 * The NgRx slice on its own cannot be the record reconciliation dedupes
 * against: Android kills a backgrounded process routinely — that is the premise
 * of this story — and the checkpoint survives that in localStorage while the
 * slice does not. Every order in the window would then reprint on the next
 * resume. Persisted next to the checkpoint so the two horizons cannot drift
 * apart. Card #4514.
 */
export const AUTOPRINT_PRINTED_ORDERS_KEY = 'autoprintRecentlyPrintedOrders';

/**
 * Widest background window foreground reconciliation looks back over.
 *
 * A device left asleep over a weekend would otherwise come back and ask for
 * every order since Friday, and reprint whichever of them had failed — long
 * after the receipt stopped being useful and the staff dealt with the order by
 * hand.
 */
export const AUTOPRINT_RECONCILIATION_MAX_LOOKBACK_MS = 6 * 60 * 60 * 1000;

/**
 * How recent an order has to be for reconciliation to leave it to the live push
 * path instead of re-queueing it.
 *
 * Between `OrderDirectPrint` being dispatched and the order arriving in the
 * autoprint queue sits the effect chain that downloads the receipt and writes
 * it to the device, which takes seconds on a phone. An order inside that gap is
 * in neither of the two records reconciliation dedupes against — not yet in the
 * queue, not yet marked printed — so re-queueing it would put a second copy of
 * the same receipt in the queue underneath the first.
 *
 * That gap is not hypothetical, it is the ordinary path: the staff member taps
 * the Android notification the push just drew, which foregrounds the app and
 * fires `appStateChange` while that same push's chain is still downloading. So
 * the newest orders are excluded and the window's upper edge sits here instead.
 * Sized well above a slow mobile invoice download rather than tuned to it.
 *
 * Measured on `submittedAt`, when the push was sent, not on `createdAt`, when
 * the customer opened the basket: an order sent after ten minutes in the basket
 * is a second old, not ten minutes old.
 */
export const AUTOPRINT_RECONCILIATION_SETTLE_MS = 60 * 1000;

/**
 * How long after the app comes back to the foreground reconciliation runs once
 * more, while the app is still open.
 *
 * The pass that runs on the way back leaves the settle band alone, and a staff
 * member opens the app within seconds of the notification — so an order whose
 * background print had already failed sat inside that band, and stayed
 * unprinted for as long as the app stayed open. One more pass after the band
 * has passed picks it up. Card #4514.
 */
export const AUTOPRINT_RECONCILIATION_FOLLOW_UP_MS =
  AUTOPRINT_RECONCILIATION_SETTLE_MS + 5 * 1000;

/**
 * How often the PayDroid build of GoAdmin asks for new orders while it is in
 * the foreground. Card #9881.
 *
 * A PAX terminal has no Google Mobile Services, so the order push that prints
 * a new order on a phone never arrives, and reconciliation is the only path
 * left to the printer. It runs on this interval for as long as GoAdmin is open
 * — which on a terminal is the whole shift; the PayDroid build keeps the
 * screen on. Together with `AUTOPRINT_PAYDROID_SETTLE_MS` this puts a new
 * order on paper 10 to 30 seconds after it is sent. Each tick costs one order
 * search per subscribed store, so the interval is the trade between how late
 * a receipt may come out and how often a terminal asks. What one search costs
 * is kept down by `AUTOPRINT_PAYDROID_PAGE_SIZE`, and a tick while nobody is
 * signed in asks nothing at all.
 */
export const AUTOPRINT_PAYDROID_POLL_MS = 20 * 1000;

/**
 * The PayDroid build's settle band: how recent an order may be and still be
 * left to the next pass. Card #9881.
 *
 * `AUTOPRINT_RECONCILIATION_SETTLE_MS` is a minute because on a phone the
 * newest orders belong to the push still downloading their receipt. A
 * terminal has no push to leave them to — the poll is the live path — so a
 * minute would only make every receipt a minute late. The band is not taken
 * to zero, though. An order is stamped `submittedAt` before the transaction
 * that submits it commits, so for a moment it can be inside a window and not
 * yet visible to the search; a pass that moved the window past it then would
 * never ask about it again. Ten seconds is ample for that, and for the push on
 * a terminal that does have Google services, where most orders have printed
 * from the push before a pass reaches them.
 *
 * It is the window edge only. The guard against dispatching an order whose
 * receipt is still downloading (`PrintersEffects.reDispatchedRecently`) stays
 * on `AUTOPRINT_RECONCILIATION_SETTLE_MS`: that wait is set by a slow download
 * on the device, which a terminal does not make any faster.
 */
export const AUTOPRINT_PAYDROID_SETTLE_MS = 10 * 1000;

/**
 * How long before its submission an order's basket may have been opened and
 * still be found.
 *
 * The order search filters on `createdAt`, the moment the basket was opened, and
 * has no `submittedAt` filter, so the window is asked for by `createdAt` this
 * much further back and narrowed to `submittedAt` here. Without it an order
 * whose basket was opened before the app went to the background, and sent while
 * it was away, was never asked about. Sized past the storefront's one-day
 * draft cookie, which lets a customer come back to the same basket the next
 * day. Pages come newest-submitted first and reading stops at the window
 * start, so the wider reach costs no extra pages. Card #4514.
 */
export const AUTOPRINT_RECONCILIATION_BASKET_AGE_MS = 48 * 60 * 60 * 1000;

/** Orders fetched per page of one store's reconciliation search. */
export const AUTOPRINT_RECONCILIATION_PAGE_SIZE = 100;

/**
 * Pages of one store's search a single pass reads before giving up on the rest
 * of that window for now. Pages come newest-submitted first and reading stops
 * at the first order older than the window, so one page is the normal case.
 */
export const AUTOPRINT_RECONCILIATION_MAX_PAGES = 20;

/**
 * Orders fetched per page of one store's reconciliation search on the PayDroid
 * build. Card #9881.
 *
 * On a phone a pass runs when the app comes back to the foreground, a few
 * times an hour at most, and its window can hold hours of orders, so a page of
 * `AUTOPRINT_RECONCILIATION_PAGE_SIZE` is worth reading. On a PAX terminal the
 * pass is the poll: every `AUTOPRINT_PAYDROID_POLL_MS` for every subscribed
 * store, all shift, over a window of the last twenty seconds or so. The search
 * cannot be narrowed to that window — it filters on when the basket was
 * opened, `AUTOPRINT_RECONCILIATION_BASKET_AGE_MS` further back — so its first
 * page is simply the newest orders of the last two days. At a hundred that
 * page made the backend load and map a hundred full orders per store per
 * terminal every twenty seconds, to find the one or two sent since the last
 * poll, and to find none at all on a quiet afternoon. Ten still finds a
 * normal window in one page: reading stops at the first order older than the
 * window, and that order is on the first page unless ten or more were sent
 * since the window began. A busier window just reads on, page after page, to
 * the same point a phone's larger pages stop at.
 */
export const AUTOPRINT_PAYDROID_PAGE_SIZE = 10;

/**
 * Pages of one store's search a single pass reads on the PayDroid build before
 * leaving the rest of the window for the next one. Card #9881.
 *
 * Raised with the smaller page so that one pass reaches exactly as far as on a
 * phone: 200 pages of `AUTOPRINT_PAYDROID_PAGE_SIZE` is the same 2000 orders a
 * store as `AUTOPRINT_RECONCILIATION_MAX_PAGES` of
 * `AUTOPRINT_RECONCILIATION_PAGE_SIZE`. The cap only comes into play after a
 * long gap — a terminal switched off over lunch, an hour without a network —
 * and then the whole gap is still read in one pass, in more requests that are
 * each a tenth of the size, instead of a tenth of it being left for later.
 */
export const AUTOPRINT_PAYDROID_MAX_PAGES = 200;

/**
 * How long the PayDroid build waits for one page of the reconciliation search
 * before giving it up. Card #9881.
 *
 * `HttpClient` has no timeout of its own, and the passes run one after another
 * (`PrintersEffects.onAutoprintAppStateChanged` is a `concatMap`), so a search
 * the network never answered held every later pass behind it: on a terminal,
 * where the poll is the only way an order reaches the printer, nothing printed
 * again until that request ended, if it ever did. Given up, it is a search
 * that failed like any other — the window is kept and the next poll asks about
 * it again. Thirty seconds is far longer than a page of
 * `AUTOPRINT_PAYDROID_PAGE_SIZE` takes on a slow connection, and short enough
 * that a terminal is printing again a poll or two after the request was lost.
 */
export const AUTOPRINT_PAYDROID_REQUEST_TIMEOUT_MS = 30 * 1000;

/**
 * The HTTP statuses that, on the PayDroid build, mean a subscribed store
 * cannot be read at all by whoever is signed in, so its failed search is
 * skipped for the pass instead of holding the window back. Card #9881.
 *
 * - 403: the signed-in user has no role at the store. A terminal is shared by
 *   the staff of a shift, and autoprint is switched on per device
 *   (`autoprintSubscriptions` is device storage, which a logout leaves alone),
 *   so the previous user's stores stay subscribed; and only the store's own
 *   Orders page can switch one off, which this user cannot open. Nothing heals
 *   that until someone with a role there signs in.
 * - 404: the store is gone.
 *
 * Any other failed store pulls the window every store shares back to its
 * start, so the next pass asks about all of it again. That is right for a
 * failure that heals, and on a phone, which reconciles once per return to the
 * foreground, it costs one re-read per return. On a terminal the pass is the
 * twenty-second poll, so one store that kept failing held the window there for
 * the whole shift: every poll re-read every other store's orders from that
 * point on, up to the six-hour floor of them in pages of ten. Skipping the
 * store lets the window move on as the stores that were read allow. What it
 * gives up is the orders such a store received meanwhile: if it becomes
 * readable again, the window has moved past them and they are not printed,
 * where a phone would recover them on its next return. They were sent to a
 * store the person at the terminal had no access to.
 *
 * Not 401: that is a token being refreshed, and it heals by itself — the
 * emulator run saw one, then a successful search on the next poll. Nor 5xx,
 * a timeout or an error with no status (offline): those heal too, and keep
 * the pull-back. A pass in which every store failed, whatever the status,
 * read no order, and the PayDroid window moves no further than the newest
 * order a search returned, so it stays where it was.
 */
export const AUTOPRINT_PAYDROID_UNREADABLE_STORE_STATUSES: readonly number[] = [
  403, 404,
];

/**
 * localStorage key holding, per store id, when autoprint was switched on for
 * that store on this device, as an ISO string.
 *
 * Reconciliation never asks a store about orders from before then: the window
 * is shared by every store, so a store switched on mid-shift would otherwise
 * have every order since the window began printed at once. A store switched on
 * before this key existed has no entry and is not limited. Card #4514.
 */
export const AUTOPRINT_SUBSCRIBED_SINCE_KEY = 'autoprintSubscribedSince';

/**
 * localStorage key holding, per `printerKey`, when the printer was added or
 * switched on for autoprint on this device, as an ISO string.
 *
 * Reconciliation never makes a printer owe an order sent before then: orders
 * that arrived while no printer was on, or before this one was registered,
 * would otherwise all come out the moment it is switched on. A printer enabled
 * before this key existed has no entry and is not limited. Card #4514.
 */
export const AUTOPRINT_PRINTER_ENABLED_SINCE_KEY =
  'autoprintPrinterEnabledSince';

export interface AutoprintState {
  subscriptions: AutoprintSubscriptions;
  autoprintQueue: AutoprintQueueElement[];
  /**
   * The orders whose receipt actually reached a printer, oldest first, pruned
   * to `AUTOPRINT_RECONCILIATION_MAX_LOOKBACK_MS` and capped at
   * `RECENTLY_PRINTED_ORDERS_CAP`.
   *
   * Foreground reconciliation re-queues every order the search endpoint
   * returns for the background window, so it needs a record of what already
   * printed or it would print the same receipt a second time. The autoprint
   * queue cannot be that record: `drainAutoPrintQueue` dequeues a job on
   * failure as well as on success, deliberately, so an order's absence from
   * the queue says nothing about whether the receipt came out. Card #4514.
   *
   * Mirrored into `AUTOPRINT_PRINTED_ORDERS_KEY` on every write, because this
   * slice does not survive the process being killed and the checkpoint that
   * triggers reconciliation does.
   */
  recentlyPrintedOrders: PrintedOrderRecord[];
}

/**
 * Folds `incoming` into `existing`: one entry per order and printer at its
 * newest `printedAt` (keeping the highest `version` either entry knew), oldest
 * first, then everything older than
 * `AUTOPRINT_RECONCILIATION_MAX_LOOKBACK_MS` before the newest entry dropped,
 * then capped.
 *
 * Pruning against the newest entry rather than against `Date.now()` keeps this
 * pure, so the reducer and the effect that persists the result agree: both need
 * the identical list or the two copies drift and the persisted one stops being a
 * usable answer to "did this order already print?".
 *
 * An entry whose `printedAt` will not parse is kept — it cannot be aged out
 * safely, and dropping it would let its order print twice — and left to the cap.
 */
export function mergePrintedOrderRecords(
  existing: PrintedOrderRecord[] | null | undefined,
  incoming: PrintedOrderRecord[] | null | undefined,
): PrintedOrderRecord[] {
  const byKey = new Map<string, PrintedOrderRecord>();
  for (const record of [...(existing ?? []), ...(incoming ?? [])]) {
    if (!record?.uuid) {
      continue;
    }
    const key = `${record.uuid}\u0000${record.printer ?? ''}`;
    const held = byKey.get(key);
    // Re-marking an order on the same printer moves it to the newest end rather
    // than adding a second entry, so one receipt can never consume two of the
    // capped slots and push a different order's record out. A stamp that
    // parses always wins over one that does not, so a record can be aged out
    // normally as soon as any write gives it a usable stamp.
    const kept = !held || wins(record, held) ? record : held;
    const other = kept === record ? held : record;
    // A printed receipt keeps the highest version any earlier attempt on that
    // printer knew: a reprint by hand knows no version, and must not erase the
    // one an automatic job carried — printed or failed, since a print after a
    // failed attempt shows at least that version — or a late copy of the same
    // push would print again. A version that was only queued is left out: it
    // may never have printed.
    const version = kept.state
      ? kept.version
      : newestVersion(
          kept,
          other && other.state !== 'queued' ? other : undefined,
        );
    const merged: PrintedOrderRecord = { ...kept };
    if (Number.isFinite(version)) {
      merged.version = version;
    } else {
      delete merged.version;
    }
    byKey.set(key, merged);
  }

  // An unparseable stamp sorts newest and is never pruned by time: it cannot be
  // aged out safely, and dropping it would let its order print a second time,
  // which is the one outcome this record exists to prevent. The cap is what
  // eventually clears it.
  const merged = [...byKey.values()].sort(
    (a, b) =>
      (printedAtOrNull(a) ?? Infinity) - (printedAtOrNull(b) ?? Infinity),
  );
  const newestAt = merged.reduce((newest, record) => {
    const at = printedAtOrNull(record);
    return at === null ? newest : Math.max(newest, at);
  }, Number.NEGATIVE_INFINITY);
  const horizon = newestAt - AUTOPRINT_RECONCILIATION_MAX_LOOKBACK_MS;
  return merged
    .filter((record) => {
      const at = printedAtOrNull(record);
      return at === null || at >= horizon;
    })
    .slice(-RECENTLY_PRINTED_ORDERS_CAP);
}

/** `null` when the stamp will not parse. */
function printedAtOrNull(record: PrintedOrderRecord): number | null {
  const at = Date.parse(record?.printedAt);
  return Number.isNaN(at) ? null : at;
}

/** The higher of the two records' versions, if either has one. */
function newestVersion(
  a: PrintedOrderRecord | undefined,
  b: PrintedOrderRecord | undefined,
): number | undefined {
  const versions = [a?.version, b?.version].filter(
    (version): version is number => Number.isFinite(version),
  );
  return versions.length ? Math.max(...versions) : undefined;
}

/** Whether `candidate` is the entry to keep for an order over `held`. */
function wins(
  candidate: PrintedOrderRecord,
  held: PrintedOrderRecord,
): boolean {
  const candidateAt = printedAtOrNull(candidate);
  const heldAt = printedAtOrNull(held);
  if (candidateAt === null) {
    return false;
  }
  return heldAt === null || candidateAt >= heldAt;
}

/** Narrows whatever came back out of storage to usable records. */
export function toPrintedOrderRecords(stored: unknown): PrintedOrderRecord[] {
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored
    .filter(
      (record): record is PrintedOrderRecord =>
        !!record &&
        typeof record === 'object' &&
        typeof (record as PrintedOrderRecord).uuid === 'string' &&
        !!(record as PrintedOrderRecord).uuid,
    )
    .map((record) => {
      // A printer that is not a string would match nothing, and a version that
      // is not a number would compare as false: drop either rather than let a
      // damaged entry stop covering its order.
      const { printer, version, state, ...rest } = record;
      return {
        ...rest,
        ...(typeof printer === 'string' ? { printer } : {}),
        ...(Number.isFinite(version) ? { version } : {}),
        ...(state === 'queued' || state === 'failed' ? { state } : {}),
      };
    });
}

/**
 * How a printer is reached.
 *
 * `Bluetooth` is Bluetooth Low Energy, the only Bluetooth transport the app
 * supported until now. `Bluetooth Classic` is SPP/RFCOMM, which is what the
 * printers embedded in Landi and iMin terminals expose — they have no GATT
 * server, so a BLE-only client cannot see them at all.
 *
 * Android only: the classic transport is served by @gonnaorder/capacitor-printer
 * and the registration dialog that offers it never opens on iOS.
 */
export type PrinterMode = 'Bluetooth' | 'Bluetooth Classic' | 'WiFi/Ethernet';

/**
 * The translation key that names each mode to the merchant.
 *
 * The saved mode is an internal value, so it is never shown as is: the
 * registration form lists the modes with these labels, and the print toasts
 * name the printer's mode with the same ones.
 */
export const PRINTER_MODE_LABELS: Record<PrinterMode, string> = {
  Bluetooth: 'admin.printer.connectionMethodBluetooth',
  'Bluetooth Classic': 'admin.printer.connectionMethodBluetoothClassic',
  'WiFi/Ethernet': 'admin.printer.connectionMethodWiFi',
};

/**
 * How one print job ends.
 *
 * `'auto'` lets the printer decide: the plugin asks it whether it has a cutter
 * and feeds instead of cutting when it says no. `'none'` is for a job that is
 * not the end of the receipt — `testPrint` sends its header and its test image
 * as two jobs, and cutting after the first would leave the image on a second,
 * severed slip. `'partial'` and `'full'` force a command form and exist for a
 * printer that needs overriding; nothing selects them automatically, because
 * the cut *shape* is a property of the blade fitted to the mechanism and not
 * something the host can choose.
 */
export type ReceiptTail = 'auto' | 'partial' | 'full' | 'none';

export interface PrinterInfo {
  name: string;
  url: string;
  type: 'AIRPRINT' | 'STAR' | 'ESC/POS';
  mode?: PrinterMode;
  ip?: string;
  port?: number;
  macAddr?: string;
  paperWidth?: number;
  enabled: boolean;
}

export interface PrintersList {
  data: PrinterInfo[];
  paging: Paging;
  totalPages: number;
}

export interface Printers {
  list: PrintersList;
  autoprintState: AutoprintState;
}

export enum PrintMode {
  PDFPRINT,
  IMAGEPRINT,
}
